import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import {
  type AudienceToken,
  getCanonicalToken,
  getStoredAudience,
  storeAudience,
  clearStoredAudience,
  isValidAudience,
} from '../constants/audienceSwapMap';

interface AudienceContextValue {
  audience: AudienceToken | null;
  setAudience: (token: string) => boolean;
  clearAudience: () => void;
}

const AudienceContext = createContext<AudienceContextValue>({
  audience: null,
  setAudience: () => false,
  clearAudience: () => {},
});

function resolveInitialAudience(): AudienceToken | null {
  try {
    const pathname = window.location.pathname.replace(/\/+$/, '');

    // On /hosts with no token, always start with no audience
    if (pathname === '/hosts') {
      clearStoredAudience();
      return null;
    }

    // On /hosts/:token, validate and use if valid
    const match = pathname.match(/^\/hosts\/([^/]+)$/i);
    if (match) {
      const canonical = getCanonicalToken(match[1]);
      if (canonical) {
        storeAudience(canonical);
        return canonical;
      }
      // Invalid token — clear any stale storage
      clearStoredAudience();
      return null;
    }
  } catch {
    // window not available
  }

  // On non-hosts pages, preserve stored audience for nav link persistence
  return getStoredAudience();
}

export function AudienceProvider({ children }: { children: ReactNode }) {
  const [audience, setAudienceState] = useState<AudienceToken | null>(
    () => resolveInitialAudience(),
  );

  const setAudience = useCallback((token: string): boolean => {
    if (!isValidAudience(token)) return false;
    const canonical = getCanonicalToken(token);
    if (!canonical) return false;
    setAudienceState(canonical);
    storeAudience(canonical);
    return true;
  }, []);

  const clearAudience = useCallback(() => {
    setAudienceState(null);
    clearStoredAudience();
  }, []);

  return (
    <AudienceContext.Provider value={{ audience, setAudience, clearAudience }}>
      {children}
    </AudienceContext.Provider>
  );
}

export function useAudience(): AudienceContextValue {
  return useContext(AudienceContext);
}
